import { useMemo } from 'react';
import { CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { InvoiceModel } from '@frontend/api-invoices';
import { getAccountLookupKey, getCareCreditLastFour, isCareCreditPayment } from './care-credit-account-utils';

export const useCareCreditAccountLastFourMap = (invoices: InvoiceModel[]) => {
  const careCreditInvoices = useMemo(
    () =>
      invoices.filter(
        (invoice) => !!invoice.locationId && !!invoice.person?.mobilePhone && isCareCreditPayment(invoice.payment)
      ),
    [invoices]
  );

  const merchantConnectionRequests = useMemo(() => {
    const invoiceLocationIds = Array.from(new Set(careCreditInvoices.map((invoice) => invoice.locationId)));
    const requests = invoiceLocationIds.map((locationId) => ({ request: { locationId } }));
    return requests;
  }, [careCreditInvoices]);

  const merchantConnectionQueries =
    CareCreditIntegrationV1.Queries.useGetMerchantConnectionQueries(merchantConnectionRequests);

  const merchantNumberByLocationId = useMemo(
    () =>
      merchantConnectionQueries.reduce<Record<string, string | undefined>>((merchantNumbers, query, index) => {
        const locationId = merchantConnectionRequests[index]?.request.locationId;
        if (locationId) {
          merchantNumbers[locationId] = query.data?.merchantNumber;
        }
        return merchantNumbers;
      }, {}),
    [merchantConnectionQueries, merchantConnectionRequests]
  );

  const accountLookupRequests = useMemo(() => {
    const lookupRequestsByKey = new Map<
      string,
      {
        request: { merchantNumber: string; phoneNumber: string };
      }
    >();

    careCreditInvoices.forEach((invoice) => {
      const merchantNumber = merchantNumberByLocationId[invoice.locationId];
      const mobilePhone = invoice.person?.mobilePhone;

      if (!merchantNumber || !mobilePhone) {
        return;
      }

      lookupRequestsByKey.set(getAccountLookupKey(merchantNumber, mobilePhone), {
        request: { merchantNumber, phoneNumber: mobilePhone },
      });
    });

    return Array.from(lookupRequestsByKey.values());
  }, [careCreditInvoices, merchantNumberByLocationId]);

  const accountLookupQueries = CareCreditIntegrationV1.Queries.useAccountLookupQueries(accountLookupRequests);

  return useMemo(() => {
    const lastFourByLookupKey = accountLookupQueries.reduce<Record<string, string | undefined>>(
      (accountLastFour, query, index) => {
        const request = accountLookupRequests[index]?.request;
        const lastFour = getCareCreditLastFour(query.data?.data?.account?.accountInfo?.accountNumber);

        if (request && lastFour) {
          accountLastFour[getAccountLookupKey(request.merchantNumber, request.phoneNumber)] = lastFour;
        }

        return accountLastFour;
      },
      {}
    );

    return careCreditInvoices.reduce<Record<string, string | undefined>>((accountLastFour, invoice) => {
      const merchantNumber = merchantNumberByLocationId[invoice.locationId];
      const mobilePhone = invoice.person?.mobilePhone;

      if (!merchantNumber || !mobilePhone) {
        return accountLastFour;
      }

      const lastFour = lastFourByLookupKey[getAccountLookupKey(merchantNumber, mobilePhone)];

      if (lastFour) {
        accountLastFour[invoice.id] = lastFour;
      }

      return accountLastFour;
    }, {});
  }, [accountLookupQueries, accountLookupRequests, careCreditInvoices, merchantNumberByLocationId]);
};
